<template>
    <div>
        <CicPopup
            id="edit_schedule"
            :title="
                accessPoint
                    ? $t('[x] Routing Schedule').replace(/\[x]/g, accessPoint.name.toString())
                    : $t(schedule.scheduleId ? 'Edit Routing Schedule' : 'New Routing Schedule')
            "
            :saveable="!!accessPoint || editing"
            class="no-padding"
            @blur="$emit('blur')"
            @input="save"
        >
            <ul>
                <template v-if="accessPoint">
                    <li class="form-label-first">
                        {{ $t('Select Routing Schedule') }}
                    </li>
                    <li>
                        <cic-select
                            id="schedule"
                            :value="schedule.scheduleId"
                            :options="scheduleOptions"
                            @input="(val) => $emit('select', val)"
                        />
                    </li>
                </template>
                <li :class="accessPoint ? 'form-label' : 'form-label-first'">
                    {{ $t('Schedule Name') }}
                </li>
                <li>
                    <cic-textbox
                        id="schedule_name"
                        :value="schedule.name"
                        :enabled="editing"
                        :maxlength="63"
                        @input="(val) => $emit('change', 'name', val)"
                    />
                </li>
                <template v-if="editing">
                    <li class="form-label">
                        {{ $t('Calendars') }}
                    </li>
                    <li>
                        <icon-button icon="calendar-icon" @click="$emit('calendar')" />
                        <icon-button icon="help-icon" :title="$t('DESC_CALENDAR')" />
                    </li>
                </template>
            </ul>
            <table id="routes_table" class="cic-table">
                <thead>
                    <tr>
                        <template v-if="editing">
                            <th>&nbsp;</th>
                            <th>&nbsp;</th>
                        </template>
                        <th>{{ $t('Daily Start Time') }}</th>
                        <th>{{ $t('Daily End Time') }}</th>
                        <th>{{ $t('Mon') }}</th>
                        <th>{{ $t('Tues') }}</th>
                        <th>{{ $t('Wed') }}</th>
                        <th>{{ $t('Thurs') }}</th>
                        <th>{{ $t('Fri') }}</th>
                        <th>{{ $t('Sat') }}</th>
                        <th>{{ $t('Sun') }}</th>
                        <th>{{ $t('Holidays') }}</th>
                        <th>{{ $t('Calendar') }}</th>
                        <th>{{ $t('Route Plan') }}</th>
                        <template v-if="editing">
                            <th>&nbsp;</th>
                        </template>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(route, routeIndex) in schedule.routes">
                        <template v-if="editing">
                            <td>
                                <icon-button
                                    v-if="routeIndex < schedule.routes.length - 1"
                                    :title="$t('Deprioritise')"
                                    icon="down-icon"
                                    class="el-icon-arrow-down"
                                    @click="move(routeIndex, 1)"
                                />
                            </td>
                            <td>
                                <icon-button
                                    v-if="routeIndex > 0"
                                    :title="$t('Prioritise')"
                                    icon="up-icon"
                                    class="el-icon-arrow-up"
                                    @click="move(routeIndex, -1)"
                                />
                            </td>
                        </template>
                        <td>
                            <cic-time-picker
                                :value="route.start"
                                :clearable="false"
                                :enabled="editing"
                                @input="(val) => updateRoute(routeIndex, 'start', val)"
                            />
                        </td>
                        <td>
                            <cic-time-picker
                                :value="route.end"
                                :clearable="false"
                                :enabled="editing"
                                @input="(val) => updateRoute(routeIndex, 'end', val)"
                            />
                        </td>
                        <td class="td-checkbox">
                            <cic-checkbox
                                :value="route.mon"
                                :enabled="editing"
                                @input="(val) => updateRoute(routeIndex, 'mon', val)"
                            />
                        </td>
                        <td class="td-checkbox">
                            <cic-checkbox
                                :value="route.tue"
                                :enabled="editing"
                                @input="(val) => updateRoute(routeIndex, 'tue', val)"
                            />
                        </td>
                        <td class="td-checkbox">
                            <cic-checkbox
                                :value="route.wed"
                                :enabled="editing"
                                @input="(val) => updateRoute(routeIndex, 'wed', val)"
                            />
                        </td>
                        <td class="td-checkbox">
                            <cic-checkbox
                                :value="route.thu"
                                :enabled="editing"
                                @input="(val) => updateRoute(routeIndex, 'thu', val)"
                            />
                        </td>
                        <td class="td-checkbox">
                            <cic-checkbox
                                :value="route.fri"
                                :enabled="editing"
                                @input="(val) => updateRoute(routeIndex, 'fri', val)"
                            />
                        </td>
                        <td class="td-checkbox">
                            <cic-checkbox
                                :value="route.sat"
                                :enabled="editing"
                                @input="(val) => updateRoute(routeIndex, 'sat', val)"
                            />
                        </td>
                        <td class="td-checkbox">
                            <cic-checkbox
                                :value="route.sun"
                                :enabled="editing"
                                @input="(val) => updateRoute(routeIndex, 'sun', val)"
                            />
                        </td>
                        <td class="td-checkbox">
                            <cic-checkbox
                                :value="route.holiday"
                                :enabled="editing"
                                @input="(val) => updateRoute(routeIndex, 'holiday', val)"
                            />
                        </td>
                        <td>
                            <cic-select
                                v-if="route.holiday"
                                :value="route.calendarId"
                                :enabled="editing"
                                :options="
                                    calendars.map((x) => {
                                        return { value: x.calendarId, label: x.name };
                                    })
                                "
                                :placeholder="$t('Please select...')"
                                @input="(val) => updateRoute(routeIndex, 'calendarId', val)"
                            />
                        </td>
                        <td>
                            <cic-select
                                :value="route.planId"
                                :enabled="editing"
                                :options="
                                    plans.map((x) => {
                                        return { value: x.planId, label: x.name };
                                    })
                                "
                                :placeholder="$t('Please select...')"
                                @input="(val) => updateRoute(routeIndex, 'planId', val)"
                            />
                        </td>
                        <template v-if="editing">
                            <td>
                                <icon-button
                                    v-if="schedule.routes.length > 1"
                                    icon="remove-icon"
                                    @click="removeRoute(routeIndex)"
                                />
                            </td>
                        </template>
                    </tr>
                    <tr v-if="editing">
                        <td colspan="14"></td>
                        <td>
                            <icon-button icon="add-icon" @click="addRoute" />
                        </td>
                    </tr>
                </tbody>
            </table>
            <template #footer>
                <cic-button
                    v-if="accessPoint && accessPoint.scheduleId > 0"
                    id="schedule_remove"
                    @click="$emit('assign', null)"
                >
                    {{ $t('Remove') }}
                </cic-button>
            </template>
        </CicPopup>
    </div>
</template>

<script>
    import CicButton from '@/components/CicButton';
    import CicPopup from '@/components/CicPopup';
    import IconButton from '@/components/controls/IconButton';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import CicTimePicker from '@/components/inputs/CicTimePicker';

    export default {
        name: 'EditRoutingSchedule',
        components: {
            CicButton,
            CicCheckbox,
            CicPopup,
            CicSelect,
            CicTextbox,
            CicTimePicker,
            IconButton,
        },
        props: {
            accessPoint: {
                type: Object,
                default: () => null,
            },

            calendars: {
                type: Array,
                required: true,
            },

            plans: {
                type: Array,
                required: true,
            },

            schedule: {
                type: Object,
                required: true,
            },

            schedules: {
                type: Array,
                required: true,
            },
        },
        emits: ['assign', 'blur', 'calendar', 'change', 'input', 'select'],
        computed: {
            editing() {
                return (
                    this.schedule &&
                    ((this.schedule.scheduleId && this.$store.hasRight('EDIT_ROUTING_SCHEDULES')) ||
                        (!this.schedule.scheduleId && this.$store.hasRight('ADD_ROUTING_SCHEDULES')))
                );
            },
            scheduleOptions() {
                let schedules = this.schedules.map((x) => ({ value: x.scheduleId, label: x.name }));
                if (this.$store.hasRight('ADD_ROUTING_SCHEDULES')) {
                    schedules = [{ value: -1, label: this.$t('[New Routing Schedule]') }].concat(schedules);
                }

                return schedules;
            },
        },
        methods: {
            addRoute() {
                const routes = JSON.parse(JSON.stringify(this.schedule.routes));
                routes.push({
                    start: '00:00:00',
                    end: '00:00:00',
                    mon: false,
                    tue: false,
                    wed: false,
                    thu: false,
                    fri: false,
                    sat: false,
                    sun: false,
                    holiday: false,
                    calendarId: null,
                    planId: null,
                });
                this.$emit('change', 'routes', routes);
            },

            move(index, increment) {
                const routes = JSON.parse(JSON.stringify(this.schedule.routes));
                routes.splice(index + increment, 0, routes.splice(index, 1)[0]);
                this.$emit('change', 'routes', routes);
            },

            removeRoute(index) {
                const routes = JSON.parse(JSON.stringify(this.schedule.routes));
                routes.splice(index, 1);
                this.$emit('change', 'routes', routes);
            },

            save() {
                if (this.editing) {
                    this.$emit('input');
                } else {
                    this.$emit('assign', this.schedule.scheduleId);
                }
            },

            updateRoute(index, field, value) {
                const routes = JSON.parse(JSON.stringify(this.schedule.routes));
                routes[index][field] = value;
                this.$emit('change', 'routes', routes);
            },
        },
    };
</script>
